import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJsApiLoader, GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import Navbar from '../components/Navbar';
import SearchBar from '../components/SearchBar';
import { useApi } from '../services/api';
import { canonicalWeekDateRange } from '../utils/forSale';
import { GOOGLE_MAPS_LIBRARIES } from '../utils/googleMaps';

const WEEK_YEAR = 2026;

const DEFAULT_CENTER = { lat: 27.6648, lng: -80.3584 };
const DEFAULT_ZOOM = 11;
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function formatCents(cents) {
  if (!cents) return null;
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

function createPropertyPin(number, hasListings, isSelected) {
  const bg = isSelected ? 'var(--brand)' : hasListings ? 'var(--success)' : 'var(--ink-faint)';
  const label = number != null ? String(number) : '';
  const fs = label.length > 2 ? 10 : 12;
  if (!label) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="27">
        <circle cx="10" cy="10" r="8" fill="${bg}" stroke="white" stroke-width="2"/>
        <polygon points="5,16 10,27 15,16" fill="${bg}"/>
      </svg>`
    )}`;
  }
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42">
      <circle cx="16" cy="16" r="14" fill="${bg}" stroke="white" stroke-width="2.5"/>
      <text x="16" y="${16 + fs / 2 + 1}" text-anchor="middle"
        font-family="Arial,sans-serif" font-weight="700" font-size="${fs}" fill="white">${label}</text>
      <polygon points="10,27 16,42 22,27" fill="${bg}"/>
    </svg>`
  )}`;
}

function MapToggleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 20l-6-3V4l6 3m0 13l6-3m-6 3V7m6 10l6 3V6l-6-3m0 17V4m0 3L9 4" />
    </svg>
  );
}

function ListToggleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function PropertyCard({ property, cardNumber, isSelected, onHover, cardRef }) {
  const navigate = useNavigate();
  const price = formatCents(property.lowest_current_price);

  return (
    <div
      ref={cardRef}
      onClick={() => navigate(`/properties/${property.slug || property.id}`)}
      onMouseEnter={() => onHover(property.id)}
      style={{
        padding: '1rem',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        background: isSelected ? 'var(--brand-light)' : 'var(--surface)',
        borderLeft: `3px solid ${isSelected ? 'var(--brand)' : 'transparent'}`,
        transition: 'background 0.15s',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'flex-start',
      }}
    >
      {/* Card number badge */}
      <div style={{
        flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
        background: isSelected ? 'var(--brand)' : 'var(--brand-dark)',
        color: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--text-xs)', fontWeight: 700, marginTop: 2,
      }}>
        {cardNumber}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {property.image_urls?.[0] && (
          <img
            src={property.image_urls[0]}
            alt={property.name}
            style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 'var(--radius-sm)', marginBottom: '0.6rem', display: 'block' }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, lineHeight: 1.3, margin: 0 }}>{property.name}</h3>
          {property.rating && (
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--warning)', whiteSpace: 'nowrap' }}>
              ★ {Number(property.rating).toFixed(1)}
            </span>
          )}
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-muted)', margin: '0.15rem 0 0.4rem' }}>
          {property.location}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {property.active_listing_count > 0 ? (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-muted)' }}>
              {property.active_listing_count} listing{property.active_listing_count !== 1 ? 's' : ''}
            </span>
          ) : (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
              No stays available right now
            </span>
          )}
          {price && (
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--brand)' }}>From {price}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Search() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q');
  const weekFrom = searchParams.get('weekFrom');
  const weekTo = searchParams.get('weekTo');
  const adults = Number(searchParams.get('adults')) || 1;
  const children = Number(searchParams.get('children')) || 0;
  const totalGuests = adults + children;

  const hasWeekFilter = Boolean(weekFrom || weekTo);
  const hasGuestFilter = totalGuests > 1;

  const { data } = useApi('/properties');
  const properties = useMemo(() => data?.properties || [], [data]);

  // Only hit /listings when a week or guest filter is active — otherwise the
  // page behaves exactly as it did before (all properties, map-bounds only).
  const listingsPath = useMemo(() => {
    if (!hasWeekFilter) return '/listings';
    const fromWeek = Number(weekFrom || weekTo);
    const toWeek = Number(weekTo || weekFrom);
    const { checkIn } = canonicalWeekDateRange(fromWeek, WEEK_YEAR);
    const { checkOut } = canonicalWeekDateRange(toWeek, WEEK_YEAR);
    return `/listings?check_in_after=${checkIn}&check_in_before=${checkOut}`;
  }, [hasWeekFilter, weekFrom, weekTo]);

  const { data: listingsData } = useApi((hasWeekFilter || hasGuestFilter) ? listingsPath : null);

  // Properties with at least one active listing matching the week range and
  // guest count — null means "no date/guest filter active", i.e. don't restrict.
  const eligiblePropertyIds = useMemo(() => {
    if (!hasWeekFilter && !hasGuestFilter) return null;
    const ids = new Set();
    for (const l of (listingsData?.listings || [])) {
      if (hasGuestFilter && l.max_guests < totalGuests) continue;
      ids.add(l.property_id);
    }
    return ids;
  }, [listingsData, hasWeekFilter, hasGuestFilter, totalGuests]);

  const hasAreaQuery = Boolean(q && q.trim());

  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'all' | 'available'

  const filteredProperties = useMemo(() => {
    let rows = properties;
    if (q) {
      const term = q.trim().toLowerCase();
      rows = rows.filter(p =>
        p.name?.toLowerCase().includes(term) ||
        p.location?.toLowerCase().includes(term) ||
        p.address?.toLowerCase().includes(term)
      );
    }
    if (eligiblePropertyIds) rows = rows.filter(p => eligiblePropertyIds.has(p.id));
    if (availabilityFilter === 'available') rows = rows.filter(p => (p.active_listing_count || 0) > 0);
    return rows;
  }, [properties, q, eligiblePropertyIds, availabilityFilter]);

  const availableNowCount = useMemo(
    () => properties.filter(p => (p.active_listing_count || 0) > 0).length,
    [properties]
  );

  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId]   = useState(null);
  const [mapBounds, setMapBounds]   = useState(null);
  const [mapLoaded, setMapLoaded]   = useState(false);
  const mapRef   = useRef(null);
  const cardRefs = useRef({});

  const isMobile = useIsMobile();

  // Mobile shows either the list or the map full-screen, toggled via the
  // floating "Show Map" / "Show List" button — never both stacked at once.
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'map'

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!mapRef.current || !q || !mapLoaded || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: q }, (results, status) => {
      if (status === 'OK' && results[0]) {
        mapRef.current.panTo(results[0].geometry.location);
        mapRef.current.setZoom(12);
      } else {
        // Silent Geocoding API failures (bad key restrictions, quota, etc.)
        // shouldn't be invisible — the results list no longer depends on this
        // succeeding, but the map itself would otherwise just silently stay put.
        console.warn('[Search] geocode failed for', JSON.stringify(q), '— status:', status);
      }
    });
  }, [q, mapLoaded]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
    setMapBounds(map.getBounds());
  }, []);

  const onIdle = useCallback(() => {
    if (mapRef.current) setMapBounds(mapRef.current.getBounds());
  }, []);

  // The map div is `display: none` while the mobile list view is showing, so
  // Google Maps never saw its real size when it first laid out — nudge it to
  // recompute as soon as the toggle switches back to the map.
  useEffect(() => {
    if (!isMobile || mobileView !== 'map' || !mapRef.current || !window.google) return;
    const raf = requestAnimationFrame(() => {
      window.google.maps.event.trigger(mapRef.current, 'resize');
    });
    return () => cancelAnimationFrame(raf);
  }, [isMobile, mobileView]);

  // When an area/location text search is active, the list should show every
  // text-matched property regardless of the map's current viewport — the map
  // itself pans to the searched location asynchronously (via geocoding), and
  // gating the list on not-yet-updated bounds could otherwise show 0 results
  // for a moment (or indefinitely, if geocoding is slow/blocked/fails).
  // Without an active query, keep the original "what's in view as I pan the
  // map" browsing behavior.
  // On mobile the map starts hidden (`display: none` behind the list/map
  // toggle), so its bounds at load time are unreliable (a 0×0 container) —
  // the list there always shows every filtered property instead.
  const visibleProperties = useMemo(() => {
    if (hasAreaQuery || isMobile) {
      return filteredProperties;
    }
    if (!mapBounds || !window.google) return filteredProperties;
    return filteredProperties.filter(p => {
      if (p.latitude == null || p.longitude == null) return true;
      return mapBounds.contains(new window.google.maps.LatLng(parseFloat(p.latitude), parseFloat(p.longitude)));
    });
  }, [filteredProperties, mapBounds, hasAreaQuery, isMobile]);

  // Map property id → its position number in the currently visible set
  const visibleIndexMap = useMemo(() => {
    const m = {};
    visibleProperties.forEach((p, i) => { m[p.id] = i + 1; });
    return m;
  }, [visibleProperties]);

  const popupProperty = useMemo(() => {
    const id = hoveredId || selectedId;
    if (!id) return null;
    return filteredProperties.find(p => p.id === id) || null;
  }, [hoveredId, selectedId, filteredProperties]);

  const handlePinClick = useCallback((id) => {
    setSelectedId(id);
    const el = cardRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const goToProperty = useCallback((idOrSlug) => navigate(`/properties/${idOrSlug}`), [navigate]);

  const handleCardHover = useCallback((id) => setSelectedId(id), []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Navbar />

      <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)', flexShrink: 0 }}>
        <SearchBar compact={isMobile} initialValues={{
          q: q || '', weekFrom, weekTo,
          date: searchParams.get('date') || '',
          adults: searchParams.get('adults'),
          children: searchParams.get('children'),
          ages: searchParams.get('ages'),
        }} />
      </div>

      {/* On mobile the list and map are two full-screen panes toggled via the
          floating button below — never shown stacked at the same time. */}
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* List: scrollable card list */}
        <div style={{
          width: isMobile ? '100%' : 380,
          flex: isMobile ? 'none' : '0 0 380px',
          display: isMobile ? (mobileView === 'list' ? 'flex' : 'none') : 'flex',
          minHeight: 0,
          overflowY: 'auto',
          borderRight: isMobile ? 'none' : '1px solid var(--border)',
          flexDirection: 'column',
          paddingBottom: isMobile ? '4.5rem' : 0,
        }}>
          <div style={{ padding: '0.65rem 1rem 0', flexShrink: 0 }}>
            <div className="pill-tabs" style={{ marginBottom: '0.65rem' }}>
              <button
                type="button"
                className={`pill-tab-btn ${availabilityFilter === 'all' ? 'active' : ''}`}
                onClick={() => setAvailabilityFilter('all')}
              >
                All Resorts
                <span className="pill-tab-count">{properties.length}</span>
              </button>
              <button
                type="button"
                className={`pill-tab-btn ${availabilityFilter === 'available' ? 'active' : ''}`}
                onClick={() => setAvailabilityFilter('available')}
              >
                Available Now
                <span className="pill-tab-count">{availableNowCount}</span>
              </button>
            </div>
          </div>

          <div style={{ padding: '0.65rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', flexShrink: 0 }}>
            {q && <><strong style={{ color: 'var(--ink)' }}>"{q}"</strong> — </>}
            {visibleProperties.length} {visibleProperties.length === 1 ? 'property' : 'properties'} in view
          </div>

          {visibleProperties.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ink-muted)', fontSize: 'var(--text-base)' }}>
              No properties in this area.<br />Try panning or zooming the map.
            </div>
          ) : (
            visibleProperties.map((p, i) => (
              <PropertyCard
                key={p.id}
                property={p}
                cardNumber={i + 1}
                isSelected={selectedId === p.id}
                onHover={handleCardHover}
                cardRef={el => { cardRefs.current[p.id] = el; }}
              />
            ))
          )}
        </div>

        {/* Map */}
        <div style={{
          flex: isMobile ? 'none' : 1,
          width: isMobile ? '100%' : 'auto',
          display: isMobile ? (mobileView === 'map' ? 'block' : 'none') : 'block',
          position: 'relative',
          touchAction: 'none',
        }}>
          {!isLoaded ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ink-muted)' }}>
              Loading map…
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              onLoad={onMapLoad}
              onIdle={onIdle}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true, zoomControl: true, gestureHandling: 'greedy' }}
            >
              {filteredProperties.map(p => {
                if (!p.latitude || !p.longitude) return null;
                const hasListings = (p.active_listing_count || 0) > 0;
                const isSelected  = selectedId === p.id;
                const number      = visibleIndexMap[p.id] || null;
                const pinSize     = number != null ? 32 : 20;
                const pinH        = number != null ? 42 : 27;
                return (
                  <MarkerF
                    key={p.id}
                    position={{ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }}
                    onClick={() => handlePinClick(p.id)}
                    onMouseOver={() => setHoveredId(p.id)}
                    onMouseOut={() => setHoveredId(null)}
                    zIndex={isSelected ? 3 : number != null ? 2 : 1}
                    icon={{
                      url: createPropertyPin(number, hasListings, isSelected),
                      scaledSize: new window.google.maps.Size(pinSize, pinH),
                      anchor: new window.google.maps.Point(pinSize / 2, pinH),
                    }}
                  />
                );
              })}

              {popupProperty && popupProperty.latitude && popupProperty.longitude && (
                <InfoWindowF
                  position={{ lat: parseFloat(popupProperty.latitude), lng: parseFloat(popupProperty.longitude) }}
                  options={{ pixelOffset: new window.google.maps.Size(0, -44), disableAutoPan: true }}
                  onCloseClick={() => { setHoveredId(null); setSelectedId(null); }}
                >
                  <div
                    onClick={() => goToProperty(popupProperty.slug || popupProperty.id)}
                    style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", minWidth: 170, maxWidth: 220, padding: '0.25rem 0.1rem', cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--brand-dark)', lineHeight: 1.3, marginBottom: '0.2rem' }}>
                      {popupProperty.name}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: 'var(--text-xs)', color: 'var(--ink-muted)', flexWrap: 'wrap' }}>
                      {popupProperty.rating && (
                        <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
                          ★ {Number(popupProperty.rating).toFixed(1)}
                          {popupProperty.review_count ? ` (${Number(popupProperty.review_count).toLocaleString()})` : ''}
                        </span>
                      )}
                      {popupProperty.active_listing_count > 0 ? (
                        <span>
                          {popupProperty.active_listing_count} listing{popupProperty.active_listing_count !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                          No stays available right now
                        </span>
                      )}
                    </div>
                    {popupProperty.lowest_current_price && (
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--brand)', marginTop: '0.2rem' }}>
                        From {formatCents(popupProperty.lowest_current_price)}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); goToProperty(popupProperty.slug || popupProperty.id); }}
                      style={{
                        marginTop: '0.5rem', width: '100%', padding: '0.4rem 0.6rem', minHeight: 34,
                        background: 'var(--brand)', color: 'var(--surface)', border: 'none', borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      View Property →
                    </button>
                  </div>
                </InfoWindowF>
              )}
            </GoogleMap>
          )}

          {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
            <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.7)', color: 'var(--surface)', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>
              Add REACT_APP_GOOGLE_MAPS_API_KEY to frontend/.env to enable the map
            </div>
          )}
        </div>

        {/* Fixed bottom map/list toggle — Airbnb-style, mobile only */}
        {isMobile && (
          <button
            type="button"
            onClick={() => setMobileView(v => (v === 'list' ? 'map' : 'list'))}
            style={{
              position: 'fixed',
              bottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'var(--ink)',
              color: 'var(--surface)',
              border: 'none',
              borderRadius: '999px',
              padding: '0.8rem 1.4rem',
              fontWeight: 700,
              fontSize: 'var(--text-sm)',
              fontFamily: 'inherit',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
              cursor: 'pointer',
            }}
          >
            {mobileView === 'list' ? (
              <>
                <MapToggleIcon /> Show Map
              </>
            ) : (
              <>
                <ListToggleIcon /> Show List
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
